import dayjs, { Dayjs } from 'dayjs';
import { Order } from './types';
import { businessDateOf, parseDate, toISOString } from '../../utils/dateUtils';

const FORECAST_MONTH_FORMAT = 'YYYY-MM';

/**
 * WP13 (owner answer to question 6.2): the month a ZK's forecast falls in.
 * The server computes it (`effectiveForecastMonth`, orderForecastMonthSql.js:
 * chosen `forecastMonth`, else the Polish-time (Europe/Warsaw) orderDate
 * month — the same value the report buckets by and the N/MM/YYYY number
 * carries, answer 6.10). The browser never derives it from orderDate, whose
 * local-time month can differ around midnight on the 1st.
 */
export const getOrderForecastMonth = (
  order: Pick<Order, 'forecastMonth' | 'effectiveForecastMonth'>
): Dayjs | null => {
  const month = order.effectiveForecastMonth || order.forecastMonth;
  if (!month) return null;
  const parsed = dayjs(`${month}-01`);
  return parsed.isValid() ? parsed.startOf('month') : null;
};

/**
 * True when saving the forecast-month editor would not change anything: the
 * picker still shows the effective month (or was cleared while no month was
 * chosen). The page then closes the editor without a PUT, so an untouched
 * default is never persisted as an explicit month.
 */
export const isForecastMonthUpdateNoop = (
  order: Pick<Order, 'forecastMonth' | 'effectiveForecastMonth'>,
  updateData: Record<string, unknown>
): boolean => {
  if (!('forecastMonth' in updateData)) return false;
  const next = updateData.forecastMonth ?? null;
  if (next === null) return !order.forecastMonth;
  return next === (order.forecastMonth || order.effectiveForecastMonth || null);
};

export type OrderDetailEditableField =
  | 'description'
  | 'notes'
  | 'orderDate'
  | 'expectedDelivery'
  | 'actualDelivery'
  | 'accountManagerId'
  | 'waybill'
  | 'proforma'
  | 'invoice'
  | 'manualNumbering'
  | 'clientOrderNumber'
  | 'supplierId'
  | 'producer'
  | 'factoryLocation'
  | 'deliveryLocation'
  | 'estimatedCompletionDate'
  | 'forecastMonth'
  | 'deliveryMode'
  | 'customerPaymentDueDate'
  | 'customerPaymentStatus'
  | 'customerPaymentNotes';

export const getOrderDetailInitialValues = (
  order: Order,
  field: OrderDetailEditableField
): Record<string, unknown> => {
  const initialValues: Record<string, unknown> = {};

  if (field === 'description') initialValues.description = order.description;
  if (field === 'notes') initialValues.notes = order.notes || '';
  if (field === 'orderDate') initialValues.orderDate = parseDate(businessDateOf(order.orderDate));
  if (field === 'expectedDelivery') initialValues.expectedDelivery = parseDate(order.expectedDelivery);
  if (field === 'actualDelivery') initialValues.actualDelivery = parseDate(order.actualDelivery);
  if (field === 'accountManagerId') initialValues.accountManagerId = order.accountManagerId || undefined;
  if (field === 'waybill') {
    initialValues.waybillNumber = order.waybillNumber || '';
    initialValues.waybillDate = parseDate(order.waybillDate);
  }
  if (field === 'proforma') {
    initialValues.proformaNumber = order.proformaNumber || '';
    initialValues.proformaDate = parseDate(order.proformaDate);
  }
  if (field === 'invoice') {
    initialValues.invoiceNumber = order.invoiceNumber || '';
    initialValues.invoiceDate = parseDate(order.invoiceDate);
  }
  if (field === 'manualNumbering') {
    initialValues.useManualNumbering = order.useManualNumbering;
    initialValues.manualOrderNumber = order.manualOrderNumber || '';
  }
  if (field === 'clientOrderNumber') initialValues.clientOrderNumber = order.clientOrderNumber || '';
  if (field === 'supplierId') initialValues.supplierId = order.supplierId || undefined;
  if (field === 'producer') initialValues.producer = order.producer || '';
  if (field === 'factoryLocation') initialValues.factoryLocation = order.factoryLocation || '';
  if (field === 'deliveryLocation') initialValues.deliveryLocation = order.deliveryLocation || '';
  if (field === 'estimatedCompletionDate') initialValues.estimatedCompletionDate = parseDate(order.estimatedCompletionDate);
  if (field === 'forecastMonth') initialValues.forecastMonth = getOrderForecastMonth(order);
  if (field === 'deliveryMode') initialValues.deliveryMode = order.deliveryMode || 'OUR_DELIVERY';
  if (field === 'customerPaymentDueDate') initialValues.customerPaymentDueDate = parseDate(order.customerPaymentDueDate);
  if (field === 'customerPaymentStatus') initialValues.customerPaymentStatus = order.customerPaymentStatus || 'PENDING';
  if (field === 'customerPaymentNotes') initialValues.customerPaymentNotes = order.customerPaymentNotes || '';

  return initialValues;
};

export const getOrderDetailFieldNames = (
  field: OrderDetailEditableField,
  values: Record<string, any> = {}
): string[] => {
  if (field === 'waybill') return ['waybillNumber', 'waybillDate'];
  if (field === 'proforma') return ['proformaNumber', 'proformaDate'];
  if (field === 'invoice') return ['invoiceNumber', 'invoiceDate'];
  if (field === 'manualNumbering') {
    return values.useManualNumbering ? ['useManualNumbering', 'manualOrderNumber'] : ['useManualNumbering'];
  }

  const singleFieldMap: Record<Exclude<OrderDetailEditableField, 'waybill' | 'proforma' | 'invoice' | 'manualNumbering'>, string> = {
    description: 'description',
    notes: 'notes',
    orderDate: 'orderDate',
    expectedDelivery: 'expectedDelivery',
    actualDelivery: 'actualDelivery',
    accountManagerId: 'accountManagerId',
    clientOrderNumber: 'clientOrderNumber',
    supplierId: 'supplierId',
    producer: 'producer',
    factoryLocation: 'factoryLocation',
    deliveryLocation: 'deliveryLocation',
    estimatedCompletionDate: 'estimatedCompletionDate',
    forecastMonth: 'forecastMonth',
    deliveryMode: 'deliveryMode',
    customerPaymentDueDate: 'customerPaymentDueDate',
    customerPaymentStatus: 'customerPaymentStatus',
    customerPaymentNotes: 'customerPaymentNotes',
  };

  return [singleFieldMap[field as keyof typeof singleFieldMap]];
};

export const buildOrderDetailUpdateData = (
  field: OrderDetailEditableField,
  values: Record<string, any>
): Record<string, unknown> => {
  const updateData: Record<string, unknown> = {};

  if (field === 'description') updateData.description = values.description;
  if (field === 'notes') updateData.notes = values.notes;
  if (field === 'orderDate') updateData.orderDate = values.orderDate?.format('YYYY-MM-DD');
  if (field === 'expectedDelivery') updateData.expectedDelivery = toISOString(values.expectedDelivery);
  if (field === 'actualDelivery') updateData.actualDelivery = toISOString(values.actualDelivery);
  if (field === 'accountManagerId') updateData.accountManagerId = values.accountManagerId || null;
  if (field === 'waybill') {
    updateData.waybillNumber = values.waybillNumber;
    updateData.waybillDate = toISOString(values.waybillDate);
  }
  if (field === 'proforma') {
    updateData.proformaNumber = values.proformaNumber;
    updateData.proformaDate = toISOString(values.proformaDate);
  }
  if (field === 'invoice') {
    updateData.invoiceNumber = values.invoiceNumber;
    updateData.invoiceDate = toISOString(values.invoiceDate);
  }
  if (field === 'manualNumbering') {
    updateData.useManualNumbering = Boolean(values.useManualNumbering);
    updateData.manualOrderNumber = values.useManualNumbering
      ? values.manualOrderNumber?.trim() || null
      : null;
  }
  if (field === 'clientOrderNumber') updateData.clientOrderNumber = values.clientOrderNumber?.trim() || null;
  if (field === 'supplierId') updateData.supplierId = values.supplierId || null;
  if (field === 'producer') updateData.producer = values.producer?.trim() || null;
  if (field === 'factoryLocation') updateData.factoryLocation = values.factoryLocation?.trim() || null;
  if (field === 'deliveryLocation') updateData.deliveryLocation = values.deliveryLocation?.trim() || null;
  if (field === 'estimatedCompletionDate') updateData.estimatedCompletionDate = toISOString(values.estimatedCompletionDate);
  // Cleared picker = null = back to the orderDate month.
  if (field === 'forecastMonth') {
    const month = values.forecastMonth ? dayjs(values.forecastMonth) : null;
    updateData.forecastMonth = month?.isValid() ? month.format(FORECAST_MONTH_FORMAT) : null;
  }
  if (field === 'deliveryMode') updateData.deliveryMode = values.deliveryMode === 'CUSTOMER_PICKUP' ? 'CUSTOMER_PICKUP' : 'OUR_DELIVERY';
  if (field === 'customerPaymentDueDate') updateData.customerPaymentDueDate = toISOString(values.customerPaymentDueDate);
  if (field === 'customerPaymentStatus') updateData.customerPaymentStatus = values.customerPaymentStatus || 'PENDING';
  if (field === 'customerPaymentNotes') updateData.customerPaymentNotes = values.customerPaymentNotes?.trim() || null;

  return updateData;
};
